import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  Clapperboard,
  Circle,
  Image as ImageIcon,
  Images,
  Info,
  Lock,
  LockOpen,
  PlayCircle,
  Save,
  Sparkles,
  Trash2,
  Type as TypeIcon,
} from 'lucide-react';
import StudioTutorialModal, { markStudioTutorialSeen, studioTutorialSeen } from '../components/StudioTutorialModal';
import { EditorSkeleton } from '../components/ExploreSkeletons';
import StudioPromptField, { rewriteSlotRefs } from '../components/StudioPromptField';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import CreditConfirmModal from '../components/fragmentador/CreditConfirmModal';
import ImageModelPicker, { defaultImageModel } from '../components/image/ImageModelPicker';
import VideoModelPicker, { type VideoModelPickerValue } from '../components/video/VideoModelPicker';
import { uploadToGallery } from '../lib/mediaGallery';
import { STAGE_JOBS_TIMEOUT_MS, waitForJobResultUrl, waitForStageJobs } from '../lib/jobsApi';
import { lowestPreviewImageModelId, pickImageModel } from '../lib/imageModelCatalog';
import { quoteTemplateCredits } from '../lib/templateQuote';
import {
  getVideoModelById,
  lowestResolution,
  pickVideoModelSelection,
  requiresDrivingAudio,
} from '../lib/videoModelCatalog';
import {
  isPlatformCreator,
  latestPreviewJob,
  remixTemplate,
  studioCall,
  type ExploreCategory,
} from '../lib/exploreTemplates';
import '../explore.css';

type SlotType = 'text' | 'image_start' | 'image_end' | 'image_ref';

type SlotDraft = {
  key: string;
  label: string;
  slot_type: SlotType;
  required: boolean;
  home_visible: boolean;
};

const ASPECTS = ['16:9', '9:16', '1:1', '4:3', '3:4'];

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function slotRef(slot: Pick<SlotDraft, 'key' | 'label'>): string {
  const name = (slot.label || slot.key).trim();
  return name ? `/${name}` : '';
}

function mentionsSlot(prompt: string, slot: Pick<SlotDraft, 'key' | 'label'>): boolean {
  if (slot.key && prompt.includes(`{{${slot.key}}}`)) return true;
  const names = [slot.label, slot.key].map((n) => n.trim()).filter(Boolean);
  return names.some((name) => new RegExp(`/${escapeRegExp(name)}(?![\\wÀ-ÿ])`, 'i').test(prompt));
}

function uniqueKey(label: string, slots: SlotDraft[], index: number): string {
  const base = slugKey(label);
  if (!base) return '';
  const taken = slots.filter((_, i) => i !== index).map((s) => s.key);
  if (!taken.includes(base)) return base;
  let n = 2;
  let next = `${base}_${n}`;
  while (taken.includes(next)) {
    n += 1;
    next = `${base}_${n}`.slice(0, 41);
  }
  return next.slice(0, 41);
}

function slugKey(label: string): string {
  const base = label
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  if (!base) return '';
  return /^[a-z]/.test(base) ? base : `p_${base}`.slice(0, 41);
}

function defaultPick(modality: 't2v' | 'i2v'): VideoModelPickerValue {
  const selection = pickVideoModelSelection({ modality });
  return {
    videoModelId: selection.videoModelId,
    resolutionId: selection.resolutionId,
    durationSeconds: Math.min(5, selection.model.durationMax || 5),
    generateAudio: false,
  };
}

export default function CreatorStudioEditor() {
  const { templateId } = useParams();
  const isNew = !templateId || templateId === 'novo';
  const navigate = useNavigate();
  const { user } = useAuth();
  const { org, refreshOrg } = useOrg();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [categories, setCategories] = useState<ExploreCategory[]>([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [aspect, setAspect] = useState('16:9');
  const [prompt, setPrompt] = useState('');
  const [brief, setBrief] = useState('');
  const [slots, setSlots] = useState<SlotDraft[]>([]);
  const [defaults, setDefaults] = useState<Record<string, string>>({});
  const [posterUrl, setPosterUrl] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [status, setStatus] = useState('draft');
  const [publishedAt, setPublishedAt] = useState<string | null>(null);
  const [id, setId] = useState<string | null>(isNew ? null : templateId || null);
  const modality = slots.some((s) => s.slot_type !== 'text') ? 'i2v' : 't2v';
  const [pick, setPick] = useState<VideoModelPickerValue>(() => defaultPick('t2v'));
  const [lockModel, setLockModel] = useState(true);
  const [adaptPrompt, setAdaptPrompt] = useState(false);
  const [adaptHelp, setAdaptHelp] = useState(false);
  const [enableStages, setEnableStages] = useState(false);
  const [stageCount, setStageCount] = useState(7);
  const [stagePrompt, setStagePrompt] = useState('');
  const [adaptStagePrompt, setAdaptStagePrompt] = useState(false);
  const [adaptStageHelp, setAdaptStageHelp] = useState(false);
  const [imageModelId, setImageModelId] = useState(() => defaultImageModel().id);
  const [lockImageModel, setLockImageModel] = useState(true);
  const [stagesIncludeUserImages, setStagesIncludeUserImages] = useState(false);
  const [stagePreviewUrls, setStagePreviewUrls] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [minDurationSeconds, setMinDurationSeconds] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [stageConfirm, setStageConfirm] = useState(false);
  const [loaded, setLoaded] = useState(isNew);
  const [tutorialOpen, setTutorialOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancel = false;
    isPlatformCreator(user.id).then((ok) => { if (!cancel) setAllowed(ok); });
    return () => { cancel = true; };
  }, [user]);

  useEffect(() => {
    if (allowed && !studioTutorialSeen()) setTutorialOpen(true);
  }, [allowed]);

  const closeTutorial = useCallback(() => {
    markStudioTutorialSeen();
    setTutorialOpen(false);
  }, []);

  useEffect(() => {
    if (!allowed || isNew || !templateId) return;
    let cancel = false;
    studioCall<{
      template: {
        id: string;
        title: string;
        description: string | null;
        category_id: string | null;
        aspect_ratio: string;
        video_model_id: string;
        resolution_id: string;
        duration_seconds: number;
        min_duration_seconds: number | null;
        generate_audio: boolean;
        lock_model: boolean;
        adapt_prompt: boolean;
        enable_stages: boolean;
        stage_count: number;
        image_model_id: string | null;
        lock_image_model: boolean;
        adapt_stage_prompt: boolean;
        stages_include_user_images: boolean;
        poster_url: string | null;
        preview_video_url: string | null;
        status: string;
        published_at: string | null;
      };
      slots: SlotDraft[];
      prompt: string;
      stage_prompt: string;
      defaults: Record<string, string>;
    }>({ action: 'get', template_id: templateId }).then((data) => {
      if (cancel) return;
      const t = data.template;
      setId(t.id);
      setTitle(t.title);
      setDescription(t.description || '');
      setCategoryId(t.category_id || '');
      setAspect(t.aspect_ratio);
      setPrompt(data.prompt || '');
      setStagePrompt(data.stage_prompt || '');
      setSlots(data.slots || []);
      setDefaults(data.defaults || {});
      setPosterUrl(t.poster_url);
      setPreviewUrl(t.preview_video_url);
      setStatus(t.status);
      setPublishedAt(t.published_at);
      setPick({
        videoModelId: t.video_model_id,
        resolutionId: t.resolution_id,
        durationSeconds: t.duration_seconds,
        generateAudio: t.generate_audio,
      });
      setMinDurationSeconds(
        t.min_duration_seconds != null && Number(t.min_duration_seconds) > 0
          ? Math.round(Number(t.min_duration_seconds))
          : null,
      );
      setLockModel(t.lock_model !== false);
      setAdaptPrompt(t.adapt_prompt === true);
      setEnableStages(t.enable_stages === true);
      setStageCount(Math.max(2, Math.min(12, Number(t.stage_count) || 7)));
      setImageModelId(t.image_model_id || defaultImageModel().id);
      setLockImageModel(t.lock_image_model !== false);
      setAdaptStagePrompt(t.adapt_stage_prompt === true);
      setStagesIncludeUserImages(t.stages_include_user_images === true);
      setLoaded(true);
    }).catch((err) => {
      if (cancel) return;
      setError(err instanceof Error ? err.message : 'Modelo não encontrado.');
      setLoaded(true);
    });
    studioCall<{ categories: ExploreCategory[] }>({ action: 'list' })
      .then((data) => { if (!cancel) setCategories(data.categories || []); })
      .catch(() => undefined);
    return () => { cancel = true; };
  }, [allowed, isNew, templateId]);

  useEffect(() => {
    if (!allowed || !id || previewUrl) return;
    let cancel = false;
    void (async () => {
      const job = await latestPreviewJob(id);
      if (!job || cancel || job.status === 'failed' || job.status === 'cancelled') return;
      try {
        if (job.status !== 'completed') setInfo('Prévia em andamento…');
        const url = await waitForJobResultUrl(job.id, { timeoutMs: 60 * 60 * 1000 });
        if (cancel) return;
        await studioCall({ action: 'set_preview', template_id: id, preview_video_url: url, poster_url: posterUrl });
        setPreviewUrl(url);
        setError(null);
        setInfo('Prévia pronta.');
      } catch (err) {
        if (!cancel) setError(err instanceof Error ? err.message : 'A prévia falhou.');
      }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, id, previewUrl]);

  useEffect(() => {
    if (!allowed || !isNew) return;
    studioCall<{ categories: ExploreCategory[] }>({ action: 'list' })
      .then((data) => setCategories(data.categories || []))
      .catch(() => undefined);
  }, [allowed, isNew]);

  useEffect(() => {
    const model = getVideoModelById(pick.videoModelId);
    if (model && model.modalities.includes(modality) && !requiresDrivingAudio(model)) return;
    setPick(defaultPick(modality));
  }, [modality, pick.videoModelId]);

  const credits = useMemo(() => quoteTemplateCredits({
    video_model_id: pick.videoModelId,
    resolution_id: pick.resolutionId,
    duration_seconds: pick.durationSeconds,
    generate_audio: Boolean(pick.generateAudio),
    enable_stages: enableStages,
    stage_count: stageCount,
    image_model_id: imageModelId,
  }), [pick, enableStages, stageCount, imageModelId]);

  const previewCredits = useMemo(() => {
    const model = getVideoModelById(pick.videoModelId);
    if (!model) return null;
    return quoteTemplateCredits({
      video_model_id: pick.videoModelId,
      resolution_id: lowestResolution(model).id,
      duration_seconds: pick.durationSeconds,
      generate_audio: Boolean(pick.generateAudio),
      enable_stages: enableStages,
      stage_count: stageCount,
      image_model_id: lowestPreviewImageModelId(imageModelId),
    });
  }, [pick, enableStages, stageCount, imageModelId]);

  const stagePhotoCount = useMemo(
    () => slots.filter((slot) => slot.slot_type !== 'text' && Boolean(defaults[slot.key])).length,
    [slots, defaults],
  );

  const stageGenerateCount = Math.max(2, Math.min(12, stageCount));

  const stagePreviewCredits = useMemo(() => {
    if (!enableStages) return null;
    const image = pickImageModel(lowestPreviewImageModelId(imageModelId));
    return image.creditsPerImage * stageGenerateCount;
  }, [enableStages, stageGenerateCount, imageModelId]);

  const hasImageDefault = stagePhotoCount > 0;

  const minDurationOptions = useMemo(() => {
    const model = getVideoModelById(pick.videoModelId);
    const modelMin = Math.max(1, model?.durationMin || 1);
    const modelMax = Math.max(modelMin, model?.durationMax || pick.durationSeconds);
    const ceiling = Math.min(modelMax, pick.durationSeconds);
    const options: number[] = [];
    for (let s = modelMin; s <= ceiling; s += 1) options.push(s);
    return { modelMin, options };
  }, [pick.videoModelId, pick.durationSeconds]);

  const payload = () => ({
    template: {
      id,
      title,
      description,
      category_id: categoryId || null,
      aspect_ratio: aspect,
      video_model_id: pick.videoModelId,
      resolution_id: pick.resolutionId,
      duration_seconds: pick.durationSeconds,
      min_duration_seconds: minDurationSeconds,
      generate_audio: Boolean(pick.generateAudio),
      lock_model: lockModel,
      adapt_prompt: adaptPrompt,
      enable_stages: enableStages,
      stage_count: stageCount,
      image_model_id: imageModelId,
      // Stages always expose the image-model picker on home / canvas.
      lock_image_model: enableStages ? false : lockImageModel,
      adapt_stage_prompt: adaptStagePrompt,
      stages_include_user_images: stagesIncludeUserImages,
      poster_url: posterUrl,
    },
    slots,
    prompt,
    stage_prompt: stagePrompt,
    defaults,
  });

  const save = async (): Promise<string> => {
    const data = await studioCall<{ id: string }>({ action: 'save', ...payload() });
    setId(data.id);
    if (isNew) navigate(`/estudio/${data.id}`, { replace: true });
    return data.id;
  };

  const onSave = async () => {
    setBusy(true);
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      await save();
      setInfo(status === 'published' ? 'Alterações gravadas.' : 'Rascunho gravado.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível gravar.');
    } finally {
      setSaving(false);
      setBusy(false);
    }
  };

  const onSuggest = async () => {
    if (!org?.id) {
      setError('Organização ainda não carregou.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const data = await studioCall<{
        prompt: string;
        title?: string;
        description?: string;
        slots?: SlotDraft[];
        credits_charged: number;
      }>({
        action: 'suggest',
        organization_id: org.id,
        brief,
        slots,
        prompt,
        title,
        description,
      });
      const incoming = Array.isArray(data.slots) ? data.slots : [];
      setSlots((prev) => {
        const next = [...prev];
        incoming.forEach((slot) => {
          const label = slot.label.trim().toLowerCase();
          if (!label || !slot.key) return;
          if (next.some((row) => row.key === slot.key || row.label.trim().toLowerCase() === label)) return;
          next.push({
            key: slot.key,
            label: slot.label.trim(),
            slot_type: slot.slot_type || 'text',
            required: slot.required !== false,
            home_visible: slot.home_visible !== false,
          });
        });
        return next;
      });
      setPrompt(data.prompt);
      const filled: string[] = [];
      if (!title.trim() && data.title?.trim()) {
        setTitle(data.title.trim().slice(0, 80));
        filled.push('título');
      }
      if (!description.trim() && data.description?.trim()) {
        setDescription(data.description.trim().slice(0, 280));
        filled.push('descrição');
      }
      const names = incoming.map((slot) => slot.label).filter(Boolean);
      const parts = [
        'Prompt atualizado',
        filled.length ? `preenchi ${filled.join(' e ')}` : '',
        names.length ? `cadastrei ${names.join(', ')}` : '',
        `${data.credits_charged} créditos`,
      ].filter(Boolean);
      setInfo(`${parts.join('. ')}.`);
      void refreshOrg(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'O agente não respondeu.');
    } finally {
      setBusy(false);
    }
  };

  const onStagePreview = async () => {
    if (!org?.id) {
      setError('Organização ainda não carregou.');
      return;
    }
    if (!hasImageDefault) {
      setError('Coloque ao menos uma imagem padrão nos parâmetros para prévia das stages.');
      setStageConfirm(false);
      return;
    }
    setBusy(true);
    setError(null);
    setStagePreviewUrls([]);
    setInfo('Gerando prévia das stages…');
    try {
      const savedId = await save();
      const stages = await remixTemplate({
        organization_id: org.id,
        template_id: savedId,
        intent: 'stages',
        values: defaults,
        for_preview: true,
      });
      const ids = stages.job_ids || [];
      const { urls, failed } = await waitForStageJobs(ids, {
        timeoutMs: STAGE_JOBS_TIMEOUT_MS,
        onPartial: (partial) => {
          const ready = partial.filter((u): u is string => Boolean(u));
          setStagePreviewUrls(ready);
          setInfo(`Stages: ${ready.length}/${ids.length} prontas…`);
        },
      });
      setStagePreviewUrls(urls);
      if (!urls.length) {
        throw new Error('Nenhuma stage concluiu. Tente de novo.');
      }
      if (failed) {
        setError(`${failed} stage(s) falharam. Mostrando as ${urls.length} que saíram.`);
        setInfo(`Prévia parcial. ${stages.credits_charged} créditos.`);
      } else {
        setInfo(`Prévia das stages pronta. ${stages.credits_charged} créditos.`);
      }
      void refreshOrg(false);
      setStageConfirm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'A prévia das stages falhou.');
    } finally {
      setBusy(false);
    }
  };

  const onPreview = async () => {
    if (!org?.id) {
      setError('Organização ainda não carregou.');
      return;
    }
    if (enableStages && !hasImageDefault) {
      setError('Coloque ao menos uma imagem padrão nos parâmetros para gerar a prévia com stages.');
      setConfirm(false);
      return;
    }
    setBusy(true);
    setError(null);
    setInfo('Gerando a prévia…');
    try {
      const savedId = await save();
      let stageBatchId: string | undefined;
      let charged = 0;
      if (enableStages) {
        setInfo('Gerando as stages da prévia…');
        setStagePreviewUrls([]);
        const stages = await remixTemplate({
          organization_id: org.id,
          template_id: savedId,
          intent: 'stages',
          values: defaults,
          for_preview: true,
        });
        stageBatchId = stages.stage_batch_id;
        charged += stages.credits_charged;
        const ids = stages.job_ids || [];
        const { urls, failed } = await waitForStageJobs(ids, {
          timeoutMs: STAGE_JOBS_TIMEOUT_MS,
          onPartial: (partial) => {
            const ready = partial.filter((u): u is string => Boolean(u));
            setStagePreviewUrls(ready);
            setInfo(`Stages: ${ready.length}/${ids.length} prontas…`);
          },
        });
        setStagePreviewUrls(urls);
        if (failed || urls.length < ids.length) {
          throw new Error(
            `Só ${urls.length}/${ids.length} stages concluíram. Gere a prévia das stages de novo antes do vídeo.`,
          );
        }
        setInfo('Gerando o vídeo da prévia…');
      }
      const job = await remixTemplate({
        organization_id: org.id,
        template_id: savedId,
        intent: 'preview',
        values: defaults,
        stage_batch_id: stageBatchId,
      });
      if (!job.job_id) throw new Error('A prévia não enfileirou o vídeo.');
      charged += job.credits_charged;
      const url = await waitForJobResultUrl(job.job_id, { timeoutMs: 60 * 60 * 1000 });
      await studioCall({ action: 'set_preview', template_id: savedId, preview_video_url: url, poster_url: posterUrl });
      setPreviewUrl(url);
      setInfo(`Prévia pronta. ${charged} créditos.`);
      void refreshOrg(false);
      setConfirm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'A prévia falhou.');
    } finally {
      setBusy(false);
    }
  };

  const onPublish = async (next: 'publish' | 'unpublish') => {
    setBusy(true);
    setError(null);
    try {
      const savedId = await save();
      await studioCall({ action: next, template_id: savedId });
      setStatus(next === 'publish' ? 'published' : 'draft');
      if (next === 'publish' && !publishedAt) setPublishedAt(new Date().toISOString());
      setInfo(next === 'publish' ? 'Publicado na home.' : 'Recolhido da home.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível publicar.');
    } finally {
      setBusy(false);
    }
  };

  const addSlot = (slot_type: SlotType) => {
    setSlots((prev) => [...prev, {
      key: '',
      label: '',
      slot_type,
      required: true,
      home_visible: true,
    }]);
  };

  const uploadDefault = async (key: string, file: File) => {
    if (!org?.id) {
      setError('Organização ainda não carregou.');
      return;
    }
    setBusy(true);
    try {
      const asset = await uploadToGallery(org.id, file, { title: key || 'padrão' });
      setDefaults((prev) => ({ ...prev, [key]: asset.video_url }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha no upload.');
    } finally {
      setBusy(false);
    }
  };

  const insertRef = (slot: SlotDraft) => {
    const token = slotRef(slot);
    if (!token) return;
    setPrompt((prev) => {
      if (mentionsSlot(prev, slot)) return prev;
      const pad = prev && !/\s$/.test(prev) ? ' ' : '';
      return `${prev}${pad}${token}`;
    });
  };

  if (allowed === null || (allowed && !loaded)) return <EditorSkeleton />;
  if (!allowed) {
    return (
      <div className="explore-page">
        <header className="explore-hero"><h1>Estúdio</h1></header>
        <div className="explore-state">
          <Clapperboard size={28} aria-hidden />
          <p className="explore-state-title">Sem acesso ao estúdio</p>
          <p className="explore-note">Peça acesso de criador a um administrador.</p>
          <Link to="/explorar" className="explore-btn">Voltar para Explorar</Link>
        </div>
      </div>
    );
  }

  const [ratioW, ratioH] = aspect.split(':').map(Number);
  const checks = [
    { ok: title.trim().length > 0, label: 'Título' },
    { ok: prompt.trim().length > 0, label: 'Prompt do vídeo' },
    { ok: !enableStages || stagePrompt.trim().length > 0, label: 'Prompt das stages' },
    { ok: Boolean(previewUrl), label: 'Prévia gerada' },
  ];
  const missingChecks = checks.filter((c) => !c.ok).map((c) => c.label);
  const canPublish = missingChecks.length === 0;
  const previewBusy = busy && (
    info === 'Gerando a prévia…'
    || info === 'Gerando as stages da prévia…'
    || info === 'Gerando o vídeo da prévia…'
    || info === 'Gerando prévia das stages…'
  );
  const stageGenerating = busy && info === 'Gerando prévia das stages…';
  const generating = previewBusy || (!previewUrl && info === 'Prévia em andamento…');
  const slotMeta: Record<SlotType, { label: string; icon: React.ReactNode }> = {
    text: { label: 'Texto', icon: <TypeIcon size={15} aria-hidden /> },
    image_start: { label: 'Imagem inicial', icon: <ImageIcon size={15} aria-hidden /> },
    image_end: { label: 'Imagem final', icon: <ImageIcon size={15} aria-hidden /> },
    image_ref: { label: 'Referência', icon: <Images size={15} aria-hidden /> },
  };

  return (
    <div className="explore-page studio-editor">
      <header className="studio-editor-head">
        <div>
          <Link to="/estudio" className="studio-back"><ArrowLeft size={14} aria-hidden /> Estúdio</Link>
          <h1>{isNew ? 'Novo modelo' : title || 'Modelo sem título'}</h1>
        </div>
        <div className="studio-editor-tools">
          <button type="button" className="explore-btn" onClick={() => setTutorialOpen(true)}>
            <PlayCircle size={16} aria-hidden /> Tutorial
          </button>
          <button
            type="button"
            className="explore-btn is-primary"
            disabled={busy}
            onClick={() => void onSave()}
          >
            <Save size={16} aria-hidden />
            {saving ? 'Salvando…' : 'Salvar'}
          </button>
          <span className={`studio-pill is-static ${status === 'published' ? 'is-live' : ''}`}>
            {status === 'published' ? 'Publicado' : 'Rascunho'}
          </span>
        </div>
      </header>
      {error && <p className="explore-error" role="alert">{error}</p>}
      {info && <p className="explore-note studio-info" role="status">{info}</p>}

      <div className="studio-layout">
        <div className="studio-main">
          <section className="studio-panel" aria-labelledby="studio-s1">
            <h2 id="studio-s1" className="studio-step"><span className="studio-step-n" aria-hidden>1</span> Parâmetros</h2>
            <p className="explore-note">
              Cada parâmetro vira um campo na home. No prompt, digite / e escolha o nome. Obrigatório sempre pede na home. Opcional aparece, mas pode ficar em branco. Valor fixo não aparece na home.
            </p>
            {slots.length === 0 && (
              <p className="studio-slot-empty">Nenhum parâmetro. O vídeo sai sempre igual.</p>
            )}
            {slots.map((slot, index) => {
              const meta = slotMeta[slot.slot_type];
              const ref = slotRef(slot);
              const used = mentionsSlot(prompt, slot);
              return (
                <div className="studio-slot" key={`${slot.slot_type}-${index}`}>
                  <div className="studio-slot-head">
                    <strong>{meta.icon} {meta.label}</strong>
                    <div className="studio-slot-tools">
                      {slot.slot_type === 'text' && ref && (
                        used ? (
                          <span className="studio-used"><Check size={13} aria-hidden /> No prompt</span>
                        ) : (
                          <button type="button" className="explore-btn is-sm" onClick={() => insertRef(slot)}>
                            Inserir {ref}
                          </button>
                        )
                      )}
                      <button
                        type="button"
                        className="explore-icon-btn"
                        aria-label={`Remover parâmetro ${slot.label || meta.label}`}
                        onClick={() => setSlots((prev) => prev.filter((_, i) => i !== index))}
                      >
                        <Trash2 size={16} aria-hidden />
                      </button>
                    </div>
                  </div>
                  <div className="ex-field">
                    <label htmlFor={`slot-label-${index}`}>Nome do parâmetro</label>
                    <input
                      id={`slot-label-${index}`}
                      type="text"
                      autoComplete="off"
                      value={slot.label}
                      placeholder="Ex.: Nome do corretor…"
                      onChange={(e) => {
                        const label = e.target.value;
                        const current = slots[index];
                        const key = uniqueKey(label, slots, index);
                        setSlots((prev) => prev.map((row, i) => i === index ? { ...row, label, key } : row));
                        setPrompt((text) => rewriteSlotRefs(text, current, { label, key }));
                        if (current.key && current.key !== key) {
                          setDefaults((d) => {
                            const next = { ...d };
                            if (current.key in next) {
                              next[key] = next[current.key];
                              delete next[current.key];
                            }
                            return next;
                          });
                        }
                      }}
                    />
                    {slot.key ? <p className="studio-key">No prompt: /{slot.label.trim() || slot.key}</p> : null}
                  </div>
                  <div className="ex-field">
                    <span className="studio-ask-label">Na home</span>
                    <div className="studio-ask" role="group" aria-label="Como a home trata este parâmetro">
                      {([
                        { id: 'required', label: 'Pedir, obrigatório' },
                        { id: 'optional', label: 'Pedir, opcional' },
                        { id: 'fixed', label: 'Valor fixo' },
                      ] as const).map((opt) => {
                        const on = opt.id === 'fixed'
                          ? !slot.home_visible
                          : slot.home_visible && (opt.id === 'required' ? slot.required : !slot.required);
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            className={on ? 'is-on' : ''}
                            aria-pressed={on}
                            onClick={() => setSlots((prev) => prev.map((row, i) => i === index
                              ? {
                                ...row,
                                home_visible: opt.id !== 'fixed',
                                required: opt.id === 'required',
                              }
                              : row))}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="ex-field">
                    {slot.slot_type === 'text' ? (
                      <>
                        <label htmlFor={`slot-default-${index}`}>{slot.home_visible ? 'Padrão' : 'Valor fixo'}</label>
                        <input
                          id={`slot-default-${index}`}
                          type="text"
                          autoComplete="off"
                          value={defaults[slot.key] || ''}
                          placeholder={slot.home_visible ? 'Usado se a pessoa deixar em branco…' : 'Texto que sempre entra no vídeo…'}
                          onChange={(e) => setDefaults((prev) => ({ ...prev, [slot.key]: e.target.value }))}
                        />
                      </>
                    ) : (
                      <>
                        <label htmlFor={`slot-default-${index}`}>{slot.home_visible ? 'Imagem padrão' : 'Imagem fixa'}</label>
                        <input
                          id={`slot-default-${index}`}
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file && slot.key) void uploadDefault(slot.key, file);
                          }}
                        />
                        {defaults[slot.key] && <p className="explore-note"><Check size={12} aria-hidden /> Imagem pronta.</p>}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
            <div className="studio-add" role="group" aria-label="Adicionar parâmetro">
              {(Object.keys(slotMeta) as SlotType[]).map((type) => (
                <button key={type} type="button" className="explore-btn" onClick={() => addSlot(type)}>
                  {slotMeta[type].icon} {slotMeta[type].label}
                </button>
              ))}
            </div>
          </section>

          <section className="studio-panel" aria-labelledby="studio-s2">
            <h2 id="studio-s2" className="studio-step"><span className="studio-step-n" aria-hidden>2</span> Stages</h2>
            <div className="studio-check-row">
              <input
                id="studio-stages"
                type="checkbox"
                checked={enableStages}
                onChange={(e) => setEnableStages(e.target.checked)}
              />
              <label htmlFor="studio-stages">Gerar stages de imagem antes do vídeo</label>
            </div>
            <p className="explore-note">
              Para gerar as stages basta o prompt e ao menos uma imagem de referência. O agente divide o brief 1:1 (um prompt por stage); as fotos entram só como referência.
            </p>
            {enableStages && (
              <>
                <div className="ex-field">
                  <span className="studio-ask-label">Imagens no vídeo</span>
                  <div className="studio-ask" role="group" aria-label="Como as stages usam as imagens do usuário">
                    <button
                      type="button"
                      className={!stagesIncludeUserImages ? 'is-on' : ''}
                      aria-pressed={!stagesIncludeUserImages}
                      onClick={() => setStagesIncludeUserImages(false)}
                    >
                      Substituir as imagens do usuário
                    </button>
                    <button
                      type="button"
                      className={stagesIncludeUserImages ? 'is-on' : ''}
                      aria-pressed={stagesIncludeUserImages}
                      onClick={() => setStagesIncludeUserImages(true)}
                    >
                      Enviar também as imagens do usuário
                    </button>
                  </div>
                  <p className="explore-note">
                    {stagesIncludeUserImages
                      ? 'O vídeo recebe as stages e as fotos que a pessoa enviou.'
                      : 'O vídeo recebe só as stages. As fotos da pessoa servem só de referência para gerar as imagens.'}
                  </p>
                </div>
                <div className="studio-two">
                  <div className="ex-field">
                    <label htmlFor="studio-stage-count">Quantidade de stages</label>
                    <input
                      id="studio-stage-count"
                      type="number"
                      min={2}
                      max={12}
                      value={stageCount}
                      onChange={(e) => setStageCount(Math.max(2, Math.min(12, Math.round(Number(e.target.value) || 7))))}
                    />
                  </div>
                  <div className="ex-field">
                    <label htmlFor="studio-image-model">Modelo de imagem</label>
                    <ImageModelPicker
                      value={imageModelId}
                      onChange={setImageModelId}
                      aria-label="Modelo de imagem das stages"
                    />
                  </div>
                </div>
                <p className="explore-note">
                  Este modelo é o padrão das stages. Na home e na Tela em branco a pessoa pode trocar antes de gerar.
                </p>
                <div className="ex-field">
                  <label htmlFor="studio-stage-prompt">Prompt das stages</label>
                  <StudioPromptField
                    id="studio-stage-prompt"
                    value={stagePrompt}
                    slots={slots}
                    onChange={setStagePrompt}
                  />
                  <p className="explore-note studio-count">{stagePrompt.length.toLocaleString('pt-BR')} / 8.000</p>
                </div>
                <div className="studio-check-row">
                  <input
                    id="studio-adapt-stage"
                    type="checkbox"
                    checked={adaptStagePrompt}
                    onChange={(e) => setAdaptStagePrompt(e.target.checked)}
                  />
                  <button
                    type="button"
                    className="studio-info-i"
                    aria-expanded={adaptStageHelp}
                    aria-controls="studio-adapt-stage-help"
                    onClick={() => setAdaptStageHelp((open) => !open)}
                  >
                    <Info size={13} aria-hidden />
                    <span className="ex-sr">Explicar adaptação do prompt das stages</span>
                  </button>
                  <label htmlFor="studio-adapt-stage">Usar fotos só para ancorar identidade no texto</label>
                </div>
                {adaptStageHelp && (
                  <p id="studio-adapt-stage-help" className="explore-note">
                    O Agente só divide o seu brief em N textos — sem mudar a intenção. Com esta opção ligada, ele pode olhar as fotos só para manter identidade; não reescreve o roteiro. Em todos os casos as fotos vão como referência no modelo de imagem. Debita créditos de chat.
                  </p>
                )}
                <p className="explore-note">
                  O Agente divide o brief em {stageGenerateCount} prompts (1 por stage), preservando a intenção. Cada prompt + as {stagePhotoCount || 1} foto{stagePhotoCount === 1 ? '' : 's'} de referência vai para o modelo de imagem.
                </p>
                <div className="studio-stage-preview-actions">
                  <button
                    type="button"
                    className="explore-btn"
                    disabled={busy || stagePreviewCredits == null || !stagePrompt.trim()}
                    onClick={() => setStageConfirm(true)}
                  >
                    {stagePreviewUrls.length ? 'Gerar nova prévia das stages' : 'Gerar prévia das stages'}
                    {stagePreviewCredits != null ? ` · ${stagePreviewCredits} cr` : ''}
                  </button>
                  <p className="explore-note">
                    Sai na menor qualidade disponível deste modelo. Use uma imagem padrão nos parâmetros. Não gera o vídeo.
                  </p>
                </div>
                {(stageGenerating || stagePreviewUrls.length > 0) && (
                  <div className="studio-stage-strip" role="list" aria-label="Prévia das stages">
                    {stageGenerating && stagePreviewUrls.length === 0
                      ? Array.from({ length: stageGenerateCount }).map((_, i) => (
                        <div key={`sk-${i}`} className="sk studio-stage-thumb" role="listitem" aria-hidden />
                      ))
                      : stagePreviewUrls.map((url, i) => (
                        <figure key={`${url}-${i}`} className="studio-stage-thumb" role="listitem">
                          <img src={url} alt={`Stage ${i + 1}`} />
                          <figcaption>{i + 1}</figcaption>
                        </figure>
                      ))}
                  </div>
                )}
              </>
            )}
          </section>

          <section className="studio-panel" aria-labelledby="studio-s3">
            <h2 id="studio-s3" className="studio-step"><span className="studio-step-n" aria-hidden>3</span> Prompt do vídeo</h2>
            <div className="ex-field">
              <label htmlFor="studio-title">Título</label>
              <input id="studio-title" name="title" type="text" autoComplete="off" value={title} maxLength={80} placeholder="Ex.: Apresentação do corretor…" onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div className="ex-field">
              <label htmlFor="studio-desc">Descrição pública</label>
              <input id="studio-desc" name="description" type="text" autoComplete="off" value={description} maxLength={280} placeholder="O que a pessoa recebe ao usar este modelo…" onChange={(e) => setDescription(e.target.value)} />
              <p className="explore-note">Aparece na home. Não coloque o prompt aqui.</p>
            </div>
            <div className="ex-field">
              <label htmlFor="studio-prompt">Prompt</label>
              <StudioPromptField
                id="studio-prompt"
                value={prompt}
                slots={slots}
                onChange={setPrompt}
              />
              <p className="explore-note studio-count">{prompt.length.toLocaleString('pt-BR')} / 8.000</p>
            </div>
            <div className="studio-check-row">
              <input
                id="studio-adapt"
                type="checkbox"
                checked={adaptPrompt}
                onChange={(e) => setAdaptPrompt(e.target.checked)}
              />
              <button
                type="button"
                className="studio-info-i"
                aria-expanded={adaptHelp}
                aria-controls="studio-adapt-help"
                onClick={() => setAdaptHelp((open) => !open)}
              >
                <Info size={13} aria-hidden />
                <span className="ex-sr">Explicar adaptação do roteiro</span>
              </button>
              <label htmlFor="studio-adapt">Adaptar o roteiro às imagens</label>
            </div>
            {adaptHelp && (
              <p id="studio-adapt-help" className="explore-note">
                Na geração, o Agente recebe o prompt original e as imagens das stages (ou os uploads, se stages estiverem desligadas). Ele reescreve o roteiro para combinar com as fotos e mantém a intenção do texto. Sem imagem, o prompt segue como você escreveu. A adaptação debita créditos de chat de quem gera o vídeo.
              </p>
            )}
            <div className="studio-gemini">
              <label htmlFor="studio-brief">Agente</label>
              <div className="studio-gemini-row">
                <input id="studio-brief" name="brief" type="text" autoComplete="off" value={brief} onChange={(e) => setBrief(e.target.value)} placeholder="Peça o vídeo ou para incluir um parâmetro…" />
                <button type="button" className="explore-btn" disabled={busy || !brief.trim()} onClick={() => void onSuggest()}>
                  <Sparkles size={15} aria-hidden /> Enviar
                </button>
              </div>
              <p className="explore-note">Reescreve o prompt, preenche título e descrição se estiverem vazios, e cadastra parâmetros novos quando o pedido pede. Debita créditos de chat da organização.</p>
            </div>
          </section>
        </div>

        <aside className="studio-aside" aria-label="Vídeo e publicação">
          <section className="studio-panel" aria-labelledby="studio-s4">
            <h2 id="studio-s4" className="studio-step"><span className="studio-step-n" aria-hidden>4</span> Vídeo</h2>
            <div
              className="studio-preview-box"
              style={{ '--ar-w': ratioW || 16, '--ar-h': ratioH || 9 } as React.CSSProperties}
            >
              {previewUrl ? (
                <video
                  key={previewUrl}
                  src={previewUrl}
                  controls
                  playsInline
                  preload="metadata"
                  aria-label="Prévia do modelo"
                  aria-busy={generating || undefined}
                  style={generating ? { opacity: 0.35 } : undefined}
                />
              ) : generating ? (
                <div className="sk explore-media-fill" role="status" aria-label="Gerando a prévia…" />
              ) : (
                <div className="studio-preview-empty">
                  <Clapperboard size={24} aria-hidden />
                  <span>A prévia aparece aqui</span>
                </div>
              )}
            </div>
            {generating && <p className="explore-note">Gerando a prévia. Pode levar alguns minutos, e você pode sair desta página.</p>}

            <div className="studio-two">
              <div className="ex-field">
                <label htmlFor="studio-cat">Tema</label>
                <select id="studio-cat" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                  <option value="">Sem tema</option>
                  {categories.map((cat) => <option key={cat.id} value={cat.id}>{cat.label}</option>)}
                </select>
              </div>
              <div className="ex-field">
                <label htmlFor="studio-aspect">Proporção</label>
                <select id="studio-aspect" value={aspect} onChange={(e) => setAspect(e.target.value)}>
                  {ASPECTS.map((value) => <option key={value} value={value}>{value}</option>)}
                </select>
              </div>
            </div>

            <VideoModelPicker
              modality={modality}
              value={pick}
              onChange={(next) => {
                const model = getVideoModelById(next.videoModelId);
                if (model && requiresDrivingAudio(model)) {
                  setError('Avatares com áudio não entram no Criar igual. Escolha outro modelo.');
                  return;
                }
                setPick(next);
                if (minDurationSeconds != null && next.durationSeconds < minDurationSeconds) {
                  setMinDurationSeconds(next.durationSeconds);
                }
              }}
              durationSeconds={pick.durationSeconds}
              onDurationChange={(seconds) => {
                setPick((prev) => ({ ...prev, durationSeconds: seconds }));
                if (minDurationSeconds != null && seconds < minDurationSeconds) {
                  setMinDurationSeconds(seconds);
                }
              }}
            />

            <div className="ex-field">
              <label htmlFor="studio-min-duration">Mínimo na home (s)</label>
              <select
                id="studio-min-duration"
                value={minDurationSeconds ?? ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (!raw) {
                    setMinDurationSeconds(null);
                    return;
                  }
                  const next = Math.round(Number(raw));
                  setMinDurationSeconds(next);
                  if (pick.durationSeconds < next) {
                    setPick((prev) => ({ ...prev, durationSeconds: next }));
                  }
                }}
              >
                <option value="">Padrão do modelo ({minDurationOptions.modelMin}s)</option>
                {minDurationOptions.options.map((s) => (
                  <option key={s} value={s}>{s}s</option>
                ))}
              </select>
              <p className="explore-note">
                {lockModel
                  ? 'Com o modelo bloqueado a home usa a duração acima. O mínimo vale se você desbloquear.'
                  : 'Na home a pessoa não escolhe menos que isso.'}
              </p>
            </div>

            <label className="studio-lock">
              <input type="checkbox" checked={lockModel} onChange={(e) => setLockModel(e.target.checked)} />
              <span className="studio-lock-text">
                <strong>{lockModel ? <Lock size={14} aria-hidden /> : <LockOpen size={14} aria-hidden />} Bloquear modelo</strong>
                <span>
                  {lockModel
                    ? 'A pessoa usa exatamente este modelo e não pode trocar.'
                    : 'A pessoa começa neste modelo e pode trocar. O preço segue a escolha dela.'}
                </span>
              </span>
            </label>

            <p className="studio-cost">
              Custo por vídeo na home
              <strong>{credits == null ? '—' : `${credits}\u00a0cr`}</strong>
            </p>
            <p className="explore-note">A prévia sai na menor qualidade deste modelo. Na home a pessoa escolhe a qualidade.</p>
          </section>

          <section className="studio-panel studio-publish" aria-labelledby="studio-s5">
            <h2 id="studio-s5" className="studio-step is-plain">Publicar</h2>
            <ul className="studio-checks">
              {checks.map((c) => (
                <li key={c.label} className={c.ok ? 'is-ok' : ''}>
                  {c.ok ? <Check size={14} aria-hidden /> : <Circle size={14} aria-hidden />}
                  {c.label}
                  <span className="ex-sr">{c.ok ? ' (ok)' : ' (pendente)'}</span>
                </li>
              ))}
            </ul>
            <div className="studio-bar">
              <button type="button" className="explore-btn" disabled={busy} onClick={() => void onSave()}>
                {saving ? 'Salvando…' : 'Salvar'}
              </button>
              <button
                type="button"
                className="explore-btn"
                disabled={busy || previewCredits == null || !prompt.trim() || (enableStages && !stagePrompt.trim())}
                onClick={() => setConfirm(true)}
              >
                {previewUrl ? 'Gerar nova prévia' : 'Gerar prévia'}{previewCredits != null ? ` · ${previewCredits} cr` : ''}
              </button>
              {status === 'published' ? (
                <button type="button" className="explore-btn" disabled={busy} onClick={() => void onPublish('unpublish')}>
                  Recolher da home
                </button>
              ) : (
                <button
                  type="button"
                  className="explore-btn is-primary"
                  disabled={busy || !canPublish}
                  aria-describedby={canPublish ? undefined : 'studio-publish-hint'}
                  onClick={() => void onPublish('publish')}
                >
                  Publicar na home
                </button>
              )}
            </div>
            {!canPublish && status !== 'published' && (
              <p id="studio-publish-hint" className="explore-note">Falta: {missingChecks.join(', ').toLowerCase()}.</p>
            )}
          </section>
        </aside>
      </div>

      <CreditConfirmModal
        open={confirm}
        title="Gerar prévia"
        subtitle={enableStages
          ? 'Gera as stages e depois o vídeo na menor qualidade. Na home a pessoa escolhe a qualidade.'
          : 'Sai na menor qualidade deste modelo. Na home a pessoa escolhe a qualidade.'}
        credits={previewCredits || 0}
        balance={Number(org?.available_credits) || 0}
        busy={busy}
        error={error}
        confirmLabel="Gerar prévia"
        onCancel={() => { if (!busy) setConfirm(false); }}
        onConfirm={() => void onPreview()}
      />
      <CreditConfirmModal
        open={stageConfirm}
        title="Gerar prévia das stages"
        subtitle={`O agente divide o brief em ${stageGenerateCount} prompts (1 por stage) e envia cada um com as fotos de referência. Créditos de chat à parte. Menor qualidade do modelo.`}
        credits={stagePreviewCredits || 0}
        balance={Number(org?.available_credits) || 0}
        busy={busy}
        error={error}
        confirmLabel="Gerar prévia das stages"
        onCancel={() => { if (!busy) setStageConfirm(false); }}
        onConfirm={() => void onStagePreview()}
      />
      <StudioTutorialModal open={tutorialOpen} onClose={closeTutorial} />
    </div>
  );
}
